import { useContext } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { StreamContext } from "contexts/Stream";
import useUpdateVariant from "hooks/useUpdateVariant";
import useGtagVariantData from "hooks/useGtagVariantData";
import useDistinctVariantTypes from "hooks/useDistinctVariantTypes";

import type { SelectChangeEvent } from "@mui/material/Select";
import {
  ANIMATIONS,
  CAMERA_PREFIX_NAMES,
  MODELS,
  MODELS_NAMES,
  STREAM_TYPES,
  SUN_STATE,
  VIEW_TYPES,
} from "shared/enum";
import { DataContext } from "contexts/Data";
import useVariant from "hooks/useVariant";

export default function ModelSelector() {
  const { setCachedState } = useContext(DataContext);

  const {
    turntableState,
    setTurntableState,
    setStreamingState,
    checkSessionAvailability,
    setStreamViewType,
    cameras,
    setDemoState,
  } = useContext(StreamContext);
  const { isTurntable } = turntableState;
  const variant = useVariant();
  const isBE6 = variant.code === MODELS["BE 6"].code;

  const models = useDistinctVariantTypes("code");
  const updateVariant = useUpdateVariant();
  const getGtagData = useGtagVariantData();

  const selectedModel = models.find(({ selected }) => selected);
  const exteriorMainCamera = cameras?.find((cam) => cam.name === "Cam_orbit_01EXT");

  const handleChange = (event: SelectChangeEvent<string>) => {
    updateVariant({ code: event.target?.value });
    window.gtag(
      "event",
      "variant_change",
      getGtagData("model", `Change variant (Model) to ${event.target.value}`),
    );
    resetAnimationsPanel(event.target?.value);

    setStreamingState((prev) => ({
      ...prev,
      switchStream: true,
    }));

    if (isTurntable) {
      setStreamingState((prev) => ({
        ...prev,
        switchStream: true,
      }));

      const model = event.target.value;
      const isM110 = model === MODELS_NAMES.m110;

      setTurntableState((prev) => ({
        ...prev,
        // selectedVariants: isM110 ? [...variant.lamps_off_variant_ext] : ["DRL_OFF"],
      }));

      return;
    }

    setDemoState((prev) => ({
      ...prev,
      dropdownChangeSwitch: true,
      selectedCameraView: {
        id: `1`,
        label: exteriorMainCamera?.id,
        default: true,
        camId: exteriorMainCamera?.id,
        cameraData: exteriorMainCamera,
      },
    }));

    setTimeout(() => {
      checkSessionAvailability(MODELS[event.target.value].id).then((data) => {
        if (data?.status === 200) {
          setStreamingState((prev) => ({
            ...prev,
            // switchStream: true,
            isStreamStarted: true,
            isStreamingAvailable: true,
            callStreamingAvailableAPI: false,
            isDontNotify: false,
          }));

          setTurntableState((prev) => ({
            ...prev,
            isTurntable: false,
          }));
        } else {
          setTurntableState((prev) => ({
            ...prev,
            isTurntable: true,
          }));
        }
        setStreamViewType(data?.status !== 200 ? STREAM_TYPES.TURNTABLE : STREAM_TYPES.STREAMING);
      });
    }, 500);
  };

  const resetAnimationsPanel = (modelName?: string) => {
    setCachedState((prev) => ({
      ...prev,
      variantId: MODELS[modelName]?.variantId,
      colorId: isBE6 ? "E0_BodyPaint_DG_Glo" : "E0_BodyPaint_TR",
      animations: {
        dayMode: true,
        doorsOpen: false,
        headlights: false,
        sunroofOpen: false,
      },
      view: VIEW_TYPES.EXTERIOR,
    }));

    setDemoState((prev) => {
      return {
        ...prev,
        animationCommands: [`${ANIMATIONS[MODELS[modelName].code].goodByeHello.name}|seek|100`],
        sunState: SUN_STATE[MODELS[variant.code].code].dayMode,
      };
    });

    setTurntableState((prev) => ({
      ...prev,
      // selectedVariants: [],
      turntableCameraNamePrefix: CAMERA_PREFIX_NAMES.EXT,
    }));
  };

  return (
    <FormControl fullWidth>
      <InputLabel id="model-select-label">Model</InputLabel>
      <Select
        labelId="model-select-label"
        id="model-select"
        value={selectedModel?.code || ""}
        label="Model"
        onChange={handleChange}
        IconComponent={ExpandMoreIcon}
      >
        {models.map((model) => (
          <MenuItem key={`model-${model.code}`} value={model.code}>
            {model.code}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
